import React, { useState, useMemo, useEffect } from 'react';
import { 
  Card, Table, Form, Input, DatePicker, 
  Button, Space, Cascader, Tag, Typography, message, Tooltip
} from 'antd';
import { 
  SearchOutlined, 
  ReloadOutlined, 
  DownloadOutlined,
} from '@ant-design/icons';

const { Text } = Typography;
const { RangePicker } = DatePicker;

// 模拟消费账户初始余额
const MOCK_INITIAL_BALANCE = -850.00;

// 模拟收支类型数据
const TRANSACTION_TYPES = [
  {
    value: 'income',
    label: '收入',
    children: [
      { value: 'recharge', label: '账户充值' },
      { value: 'refund', label: '套餐底价退回' },
      { value: 'offset_income', label: '冲账' },
    ],
  },
  {
    value: 'expense',
    label: '支出',
    children: [
      { value: 'package_cost', label: '套餐底价' },
      { value: 'auth_purchase', label: '授权采购' },
      { value: 'offset_expense', label: '冲账' },
      {
        value: 'private_cloud',
        label: '私有云费用',
        children: [
          { value: 'cloud_host', label: '云主机月账单' },
          { value: 'cdn', label: 'CDN流量' },
          { value: 'oss', label: 'OSS存储' },
          { value: 'bandwidth', label: '动态带宽' },
        ],
      },
      {
        value: 'public_cloud',
        label: '公有云资源',
        children: [
          { value: 'device_traffic', label: '设备流量' },
        ],
      },
      {
        value: 'platform_commission',
        label: '平台抽佣',
        children: [
          { value: 'value_added_commission', label: '增值抽佣' },
          { value: 'ad_commission', label: '广告抽佣' },
        ],
      },
    ],
  },
];

// 模拟收支流水（summary 为系统按收支类型自动生成的「交易摘要」，TODO: 联调时替换为接口数据）
const MOCK_TXNS = [
  { id: '1', type: ['income', 'recharge'], typeLabel: '收入-账户充值', summary: '线下汇款充值', currency: 'CNY', amount: 1000.00, rate: 1.00, time: '2026-03-17 10:00:00', orderNo: 'REC20260317001' },
  { id: '2', type: ['expense', 'package_cost'], typeLabel: '支出-套餐底价', summary: '套餐「车载流量年包」(PKG100001) 底价扣除（App 自收款）', currency: 'USD', amount: -50.00, rate: 7.21, time: '2026-03-16 14:30:00', orderNo: 'ORD20260316882' },
  { id: '3', type: ['income', 'refund'], typeLabel: '收入-套餐底价退回', summary: '套餐「车载流量年包」(PKG100001) 底价退回', currency: 'CNY', amount: 88.50, rate: 1.00, time: '2026-03-15 09:12:45', orderNo: 'REF20260315991' },
  { id: '4', type: ['expense', 'auth_purchase'], typeLabel: '支出-授权采购', summary: '产品「鹤梦云 IoT 模组」授权采购 ×100', currency: 'CNY', amount: -200.00, rate: 1.00, time: '2026-03-14 16:20:00', orderNo: 'PUR20260314004' },
  { id: '5', type: ['expense', 'platform_commission', 'value_added_commission'], typeLabel: '支出-平台抽佣-增值抽佣', summary: '2026-06 增值抽佣', currency: 'CNY', amount: -35.00, rate: 1.00, time: '2026-03-13 11:45:00', orderNo: 'COM20260313005' },
  { id: '6', type: ['income', 'offset_income'], typeLabel: '收入-冲账', summary: '平台手动调账（操作人：admin）', currency: 'CNY', amount: 500.00, rate: 1.00, time: '2026-03-12 10:00:00', orderNo: 'OFF20260312006' },
  { id: '7', type: ['expense', 'offset_expense'], typeLabel: '支出-冲账', summary: '平台手动调账（操作人：admin）', currency: 'CNY', amount: -200.00, rate: 1.00, time: '2026-03-11 14:00:00', orderNo: 'OFF20260311007' },
  { id: '8', type: ['expense', 'private_cloud', 'cloud_host'], typeLabel: '支出-私有云费用-云主机月账单', summary: '云主机 CVM-8827 2026-06 月账单', currency: 'CNY', amount: -1651.61, rate: 1.00, time: '2026-03-10 09:00:00', orderNo: 'CLOUD-CVM-20260401' },
  { id: '9', type: ['expense', 'private_cloud', 'cdn'], typeLabel: '支出-私有云费用-CDN流量', summary: 'CDN 流量 2026-07 账单', currency: 'CNY', amount: -84.32, rate: 1.00, time: '2026-03-09 15:30:00', orderNo: 'CLOUD-CDN-20260717' },
  { id: '10', type: ['expense', 'private_cloud', 'oss'], typeLabel: '支出-私有云费用-OSS存储', summary: 'OSS 存储 2026-07 账单', currency: 'CNY', amount: -74.07, rate: 1.00, time: '2026-03-08 11:00:00', orderNo: 'CLOUD-OSS-20260715' },
  { id: '11', type: ['expense', 'private_cloud', 'bandwidth'], typeLabel: '支出-私有云费用-动态带宽', summary: '动态带宽 2026-07 账单', currency: 'CNY', amount: -45.12, rate: 1.00, time: '2026-03-07 17:45:00', orderNo: 'CLOUD-BW-20260719' },
  { id: '12', type: ['expense', 'public_cloud', 'device_traffic'], typeLabel: '支出-公有云资源-设备流量', summary: '2026-09 设备流量账单', currency: 'CNY', amount: -1318.53, rate: 1.00, time: '2026-09-06 08:00:00', orderNo: 'TRAFFIC20260906' },
  { id: '13', type: ['expense', 'platform_commission', 'ad_commission'], typeLabel: '支出-平台抽佣-广告抽佣', summary: 'App「HomeStar」-穿山甲 2026-09 广告抽佣', currency: 'CNY', amount: -279.00, rate: 1.00, time: '2026-09-06 09:00:00', orderNo: 'ADCOM20260906' },
];

const TransactionDetails = ({ initialType }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // 生效的筛选条件（点击查询后才应用）
  const [filters, setFilters] = useState({ orderNo: '', summary: '', type: null, timeRange: null });

  // 外部跳转进入时预选收支类型（如「私有云费用」）
  useEffect(() => {
    if (initialType && initialType.length) {
      form.setFieldValue('type', initialType);
      setFilters((prev) => ({ ...prev, type: initialType }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 计算小计和总计
  const { subTotalCNY, totalCNY } = useMemo(() => {
    const sub = MOCK_TXNS.reduce((acc, curr) => acc + (curr.amount * curr.rate), 0);
    return {
      subTotalCNY: sub.toFixed(2),
      totalCNY: sub.toFixed(2) // 模拟总计与小计一致
    };
  }, []);

  // 按时间正序计算逐条运行余额
  const balanceMap = useMemo(() => {
    const sorted = [...MOCK_TXNS].sort((a, b) => new Date(a.time) - new Date(b.time));
    let running = MOCK_INITIAL_BALANCE;
    const map = {};
    sorted.forEach((r) => {
      running += r.amount * r.rate;
      map[r.id] = parseFloat(running.toFixed(2));
    });
    return map;
  }, []);

  // 按筛选条件过滤：业务单号与交易摘要分别独立匹配
  const filteredData = useMemo(() => {
    const { orderNo, summary, type, timeRange } = filters;
    return MOCK_TXNS.filter((r) => {
      if (orderNo && !(r.orderNo || '').toLowerCase().includes(orderNo.toLowerCase())) return false;
      if (summary && !(r.summary || '').toLowerCase().includes(summary.toLowerCase())) return false;
      if (type && type.length) {
        const matched = type.some((path) => path.every((seg, i) => r.type[i] === seg));
        if (!matched) return false;
      }
      if (timeRange && timeRange.length === 2) {
        const t = new Date(r.time).getTime();
        if (t < timeRange[0].valueOf() || t > timeRange[1].valueOf()) return false;
      }
      return true;
    });
  }, [filters]);

  const columns = [
    {
      title: '收支类型',
      dataIndex: 'typeLabel',
      key: 'typeLabel',
      render: (text, record) => {
        const isIncome = record.type[0] === 'income';
        return <Tag color={isIncome ? 'success' : 'error'}>{text}</Tag>;
      }
    },
    {
      title: '交易摘要',
      dataIndex: 'summary',
      key: 'summary',
      width: 240,
      ellipsis: { showTitle: false },
      render: (text) => <Tooltip title={text} placement="topLeft">{text}</Tooltip>,
    },
    { title: '交易币种', dataIndex: 'currency', key: 'currency', align: 'center' },
    { 
      title: '交易金额', 
      dataIndex: 'amount', 
      key: 'amount',
      align: 'right',
      render: (val) => {
        const isPositive = val > 0;
        return (
          <Text strong style={{ color: isPositive ? '#52c41a' : '#ff4d4f' }}>
            {isPositive ? `+${val.toFixed(2)}` : val.toFixed(2)}
          </Text>
        );
      }
    },
    { title: '结算汇率', dataIndex: 'rate', key: 'rate', align: 'right' },
    { 
      title: '结算CNY', 
      key: 'cny',
      align: 'right',
      render: (_, record) => {
        const cny = record.amount * record.rate;
        return <Text strong>¥ {cny.toFixed(2)}</Text>;
      }
    },
    {
      title: '账户余额CNY',
      key: 'balance',
      align: 'right',
      render: (_, record) => {
        const bal = balanceMap[record.id];
        const isPositive = bal >= 0;
        return <Text strong style={{ color: isPositive ? '#52c41a' : '#ff4d4f' }}>¥ {bal.toFixed(2)}</Text>;
      }
    },
    { title: '收支时间', dataIndex: 'time', key: 'time' },
    { title: '业务单号', dataIndex: 'orderNo', key: 'orderNo' },
  ];

  const handleSearch = (values) => {
    setLoading(true);
    setFilters({
      orderNo: (values.orderNo || '').trim(),
      summary: (values.summary || '').trim(),
      type: values.type && values.type.length ? values.type : null,
      timeRange: values.timeRange || null,
    });
    setTimeout(() => setLoading(false), 500);
  };

  const handleReset = () => {
    form.resetFields();
    setFilters({ orderNo: '', summary: '', type: null, timeRange: null });
  };

  const handleExport = () => {
    message.loading('正在导出数据...');
    setTimeout(() => message.success('导出成功'), 1000);
  };

  return (
    <>
      <Card bordered={false} style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline" onFinish={handleSearch}>
          <Form.Item name="orderNo">
            <Input placeholder="业务单号" allowClear style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="summary">
            <Input placeholder="交易摘要" allowClear style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="type">
            <Cascader 
              options={TRANSACTION_TYPES} 
              placeholder="收支类型" 
              multiple
              showCheckedStrategy={Cascader.SHOW_CHILD}
              maxTagCount="responsive"
              style={{ width: 260 }}
            />
          </Form.Item>
          <Form.Item name="timeRange">
            <RangePicker showTime placeholder={['开始时间', '结束时间']} style={{ width: 380 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} htmlType="submit">查询</Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card 
        bordered={false} 
        title="收支明细列表"
        extra={<Button icon={<DownloadOutlined />} onClick={handleExport}>导出数据</Button>}
      >
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', gap: 24, background: '#fafafa', padding: '12px 24px', borderRadius: 8 }}>
          <Space>
            <Text type="secondary">当前页小计 (CNY):</Text>
            <Text strong style={{ fontSize: 16 }}>¥ {subTotalCNY}</Text>
          </Space>
          <Space>
            <Text type="secondary">总计 (CNY):</Text>
            <Text strong style={{ fontSize: 16, color: '#1890ff' }}>¥ {totalCNY}</Text>
          </Space>
        </div>

        <Table 
          columns={columns} 
          dataSource={filteredData} 
          rowKey="id" 
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
        />
      </Card>
    </>
  );
};

export default TransactionDetails;
